import { useCallback, useEffect, useState } from "react";

// Sprint 5: shortlist + sent-outreach tracking lives in localStorage. Sprint 6
// migrates to Supabase once auth lands; the schema is already defined in
// supabase/migrations/0001_initial_schema.sql.

export function useLocalStorage<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readJSON(key, initial));

  useEffect(() => {
    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== key) return;
      setValue(readJSON(key, initial));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, initial]);

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // quota or disabled — ignore; in-memory state still updates.
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update];
}

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
